/**
 * Workspace Composer landscape. The caller supplies installed pack counts
 * and platform-authored review drafts from the workspace-scoped API.
 *
 * Two views:
 *   • Executive — five KPI tiles above the map, aimed at C-grade audiences
 *     (modules, entities modelled, SHACL constraints, standards aligned,
 *     regulatory frameworks encoded).
 *   • Technical — the same map with module chips and Radix Tooltip
 *     drill-downs listing real class names per module.
 *
 * Built on: @radix-ui/react-tabs (view toggle), @radix-ui/react-tooltip
 * (accessible hover explanations), lucide-react (icons). No hand-rolled
 * hovercards, no ad-hoc modals.
 */
import type { ReactNode } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  BookOpen, Building2, CircleDashed, Globe2, Landmark, Layers,
  Package, ShieldCheck, TrendingUp,
} from "lucide-react";
import { MicroLabel } from "@/components/DataPrimitives";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ModuleState = "delivered" | "draft" | "planned";

export interface OntologyModule {
  /** on-disk pack slug, e.g. "party-core". */
  slug: string;
  /** Short display name shown on the chip, e.g. "Party". */
  name: string;
  /** Counted owl:Class plus owl:ObjectProperty/datatypeProperty statements. */
  entities: number;
  /** SHACL NodeShape/PropertyShape count in the paired shapes file. */
  shapes?: number;
  /** Real class names from the TTL — shown in the hover tooltip. */
  examples: string[];
  /** Optional one-liner shown in the tooltip footer. */
  align?: string;
  state: ModuleState;
  countsAvailable?: boolean;
  version?: string;
  unit?: string;
}

export type TierTone =
  | "foundation" | "universal" | "standard" | "industry"
  | "lob" | "regional" | "customer";

export interface TierRow {
  /** Tier code, e.g. "0.5". */
  code: string;
  label: string;
  sublabel: string;
  tone: TierTone;
  Icon: typeof Layers;
  modules: OntologyModule[];
}

export interface ExecutiveKpi {
  value: string;
  label: string;
  hint?: string;
}

export interface LandscapeSpec {
  industry: string;
  tiers: TierRow[];
  kpis: ExecutiveKpi[];
  /** Aside line explaining the shared substrate proof point. */
  reuseNote: string;
}

// ---------------------------------------------------------------------------
// Tone → palette
// ---------------------------------------------------------------------------

const TONE_STYLES: Record<TierTone, { bar: string; badge: string; chip: string }> = {
  foundation: {
    bar: "from-text/20 via-text/10 to-transparent",
    badge: "border-text/30 bg-text/10 text-text",
    chip: "border-border bg-panel/60",
  },
  universal: {
    bar: "from-good/40 via-good/15 to-transparent",
    badge: "border-good/40 bg-good/10 text-good",
    chip: "border-good/25 bg-good/[0.06]",
  },
  standard: {
    bar: "from-accent/30 via-accent/10 to-transparent",
    badge: "border-accent/30 bg-accent/10 text-accent",
    chip: "border-accent/20 bg-accent/[0.04]",
  },
  industry: {
    bar: "from-accent/60 via-accent/25 to-transparent",
    badge: "border-accent/50 bg-accent/15 text-accent",
    chip: "border-accent/35 bg-accent/[0.08]",
  },
  lob: {
    bar: "from-warn/30 via-warn/10 to-transparent",
    badge: "border-warn/40 bg-warn/10 text-warn",
    chip: "border-border bg-panel/40",
  },
  regional: {
    bar: "from-warn/25 via-warn/8 to-transparent",
    badge: "border-warn/40 bg-warn/10 text-warn",
    chip: "border-border bg-panel/40",
  },
  customer: {
    bar: "from-muted/30 via-muted/10 to-transparent",
    badge: "border-muted/40 bg-muted/10 text-muted",
    chip: "border-dashed border-border bg-panel/30",
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiTile({ value, label, hint }: ExecutiveKpi) {
  return (
    <div className="stat-tile">
      <div className="stat-tile-value stat-mono">{value}</div>
      <div className="stat-tile-label">{label}</div>
      {hint && <div className="mt-1 text-[10px] leading-snug text-muted/80">{hint}</div>}
    </div>
  );
}

function ModuleChip({ m, tone }: { m: OntologyModule; tone: TierTone }) {
  const style = TONE_STYLES[tone];
  const Icon = m.state === "delivered" ? Package : CircleDashed;
  return (
    <Tooltip.Root delayDuration={80}>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          className={`group flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
            m.state === "delivered" ? style.chip : "border-dashed border-border/70 bg-panel/20"
          } hover:border-accent/60 hover:bg-accent/[0.05]`}
        >
          <Icon
            size={12}
            className={m.state === "delivered" ? "shrink-0 text-good" : "shrink-0 text-muted"}
            aria-hidden
          />
          <span className="text-[12px] font-medium leading-none text-text/95">{m.name}</span>
          {m.version && <span className="stat-mono text-[9px] text-muted">v{m.version}</span>}
          {m.countsAvailable && (
            <span className="stat-mono ml-1 text-[10.5px] text-muted">{m.entities}</span>
          )}
          <span className="stat-mono text-[9px] uppercase text-muted">
            {m.state === "draft" ? "Draft: SME review needed" : m.state}
          </span>
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          align="center"
          sideOffset={6}
          className="z-[120] max-w-[320px] rounded-md border border-border bg-panel p-3 text-[12px] leading-relaxed text-text shadow-lg shadow-black/40"
        >
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[13px] font-semibold">{m.name}</span>
            <span className="stat-mono rounded border border-border bg-bg/60 px-1.5 py-0.5 text-[10px] text-muted">
              {m.slug}
            </span>
          </div>
          {m.countsAvailable ? (
            <>
              <div className="stat-mono mb-1.5 flex items-center gap-3 text-[11px] text-muted">
                <span>{m.entities} {m.unit ?? "classes and properties"}</span>
                {typeof m.shapes === "number" && <span>{m.shapes} SHACL shapes</span>}
              </div>
              {m.examples.length > 0 && (
                <div className="text-[11.5px] leading-snug text-text/90">
                  <span className="text-muted">Includes:</span>{" "}
                  <span className="stat-mono">{m.examples.join(" · ")}</span>
                </div>
              )}
            </>
          ) : (
            <div className="text-[11.5px] leading-snug text-muted">
              No parseable ontology artifact is installed for this pack.
            </div>
          )}
          {m.align && (
            <div className="hairline-t mt-2 pt-2 text-[10.5px] uppercase tracking-wider text-muted">
              Aligns to · <span className="normal-case tracking-normal text-text/90">{m.align}</span>
            </div>
          )}
          <Tooltip.Arrow className="fill-panel" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function TierStripe({ row }: { row: TierRow }) {
  const style = TONE_STYLES[row.tone];
  return (
    <div className="rounded-lg border border-border bg-bg/40 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`stat-mono rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${style.badge}`}
        >
          T{row.code}
        </span>
        <row.Icon size={13} className="text-muted" aria-hidden />
        <span className="text-[12.5px] font-semibold leading-none text-text">{row.label}</span>
        <span className="truncate text-[11px] leading-none text-muted">· {row.sublabel}</span>
      </div>
      <div
        className={`mb-2 h-[2px] w-full rounded-full bg-gradient-to-r ${style.bar}`}
        aria-hidden
      />
      {row.modules.length === 0 ? (
        <p className="text-[11px] italic text-muted">No installed pack in this tier.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {row.modules.map((m) => (
            <ModuleChip key={m.slug} m={m} tone={row.tone} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function OntologyLandscape({
  spec,
  header,
}: {
  spec: LandscapeSpec;
  /** Optional slot for controls (industry toggle, About button) rendered
   *  inside the landscape header. */
  header?: ReactNode;
}) {
  return (
    <Tooltip.Provider delayDuration={80} disableHoverableContent>
      <div className="rounded-lg border border-border bg-panel/40 p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <MicroLabel className="flex items-center gap-1">
              <Layers size={11} /> Ontology landscape
            </MicroLabel>
            <p className="mt-0.5 text-[12px] leading-snug text-muted">
              Counts come from installed pack artifacts. Platform-authored drafts need SME review before delivery.
            </p>
          </div>
          {header}
        </div>

        <Tabs.Root defaultValue="executive">
          <Tabs.List className="mb-3 flex gap-1 rounded-md border border-border bg-bg/60 p-0.5">
            <Tabs.Trigger
              value="executive"
              className="stat-mono flex-1 rounded px-2.5 py-1 text-[10.5px] uppercase tracking-wider text-muted transition-colors data-[state=active]:bg-accent/15 data-[state=active]:text-accent"
            >
              <span className="mr-1 inline-flex items-center gap-1"><TrendingUp size={11} /> Executive</span>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="technical"
              className="stat-mono flex-1 rounded px-2.5 py-1 text-[10.5px] uppercase tracking-wider text-muted transition-colors data-[state=active]:bg-accent/15 data-[state=active]:text-accent"
            >
              <span className="mr-1 inline-flex items-center gap-1"><BookOpen size={11} /> Technical</span>
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="executive">
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3 xl:grid-cols-5">
              {spec.kpis.map((k) => (
                <KpiTile key={k.label} {...k} />
              ))}
            </div>
            <p className="hairline-t mt-3 pt-2.5 text-[11px] leading-relaxed text-muted">
              {spec.reuseNote}
            </p>
          </Tabs.Content>

          <Tabs.Content value="technical" className="space-y-2">
            {spec.tiers.map((row) => (
              <TierStripe key={row.code} row={row} />
            ))}
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </Tooltip.Provider>
  );
}

// Re-export commonly used icons so callers building `LandscapeSpec`s
// don't need a separate lucide-react import when composing tier rows.
export const TierIcons = {
  Layers, Building2, Package, Globe2, Landmark, ShieldCheck, BookOpen,
};
